import { ActionTree } from 'vuex';
import { UsersState, RootState } from '../types';
import { state as authState } from '../auth/auth';
import { AxiosInstance } from 'axios';
import { handleAllErrors } from '@/utils/errorHandling';

// Auth0's search index takes a moment to reflect user mutations (create / update / delete).
// Without this wait, a refetch immediately after a change in the users database can return old/stale results.
const AUTH0_INDEX_DELAY_MS = 1200;
const waitForAuth0Index = (): Promise<void> => new Promise(resolve => setTimeout(resolve, AUTH0_INDEX_DELAY_MS));

export const actions: ActionTree<UsersState, RootState> = {
	async fetchUsers({ commit }, params?: FetchUsersFilters & { commitToStore?: boolean }): Promise<Auth0User[] | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		const { commitToStore = true, ...filters } = params ?? {};

		try {
			const { data } = await axiosInst.get<{ users: Auth0User[]; count: number; }>('/users', { params: filters });
			if (commitToStore) {
				commit('SET_USERS', data.users);
				commit('SET_COUNT', data.count);
			}
			return data.users;
		}
		catch (error) {
			if (commitToStore) {
				commit('SET_USERS', []);
				commit('SET_COUNT', 0);
			}
			handleAllErrors(error);
		}
	},

	async fetchUserById(_, userId: string): Promise<Auth0User | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<Auth0User>(`/users/${userId}`);
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async fetchLocationAssignments(): Promise<LocationAssignment[] | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.get<LocationAssignment[]>('/users/location-assignments');
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async createUser(_, payload: CreateUserPayload): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.post('/users', payload);
			await waitForAuth0Index();
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async updateUser(_, { userId, payload }: { userId: string; payload: UpdateUserPayload }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.put(`/users/${userId}`, payload);
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async updateUserPassword(_, { userId, password }: { userId: string; password: string }): Promise<void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			await axiosInst.put(`/users/${userId}/password`, { password });
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async deleteUsers(_, userIds: string[]): Promise<BulkUserActionResult | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			if (userIds.length === 1) {
				await axiosInst.delete(`/users/${userIds[0]}`);
				await waitForAuth0Index();
				return;
			}
			const { data } = await axiosInst.post<BulkUserActionResult>('/users/bulk', { action: 'delete', userIds });
			await waitForAuth0Index();
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	},

	async bulkUpdateRoles(_, payload: { userIds: string[]; role: string; }): Promise<BulkUserActionResult | void> {
		const axiosInst: AxiosInstance = authState.axiosInst;
		try {
			const { data } = await axiosInst.post<BulkUserActionResult>('/users/bulk', { action: 'update', ...payload });
			await waitForAuth0Index();
			return data;
		}
		catch (error) {
			handleAllErrors(error);
		}
	}
};
